import { useState, useMemo } from "react";

const TIPOS_DESPIDO = [
  {
    id: "arbitrario",
    etiqueta: "Despido arbitrario",
    descripcion: "Sin causa justa comprobable en un procedimiento previo.",
    factor: 1,
  },
  {
    id: "hostilizacion",
    etiqueta: "Hostilización laboral",
    descripcion: "Actos de hostilidad equiparables a despido si optas por dar por terminado el vínculo.",
    factor: 1,
  },
  {
    id: "nulo",
    etiqueta: "Despido nulo",
    descripcion: "Por afiliación sindical, discriminación, embarazo u otras causales del Art. 29 de la LPCL.",
    factor: 2,
  },
  {
    id: "justificado",
    etiqueta: "Despido justificado (falta grave)",
    descripcion: "Causa justa acreditada: no genera derecho a indemnización.",
    factor: 0,
  },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function diferenciaFechas(inicioStr, finStr) {
  if (!inicioStr || !finStr) return { años: 0, meses: 0, dias: 0, dias360: 0 };
  const inicio = new Date(inicioStr + "T00:00:00");
  const fin = new Date(finStr + "T00:00:00");
  if (isNaN(inicio) || isNaN(fin) || fin <= inicio) {
    return { años: 0, meses: 0, dias: 0, dias360: 0 };
  }
  let años = fin.getFullYear() - inicio.getFullYear();
  let meses = fin.getMonth() - inicio.getMonth();
  let dias = fin.getDate() - inicio.getDate();
  if (dias < 0) {
    meses -= 1;
    const mesAnterior = new Date(fin.getFullYear(), fin.getMonth(), 0);
    dias += mesAnterior.getDate();
  }
  if (meses < 0) {
    años -= 1;
    meses += 12;
  }
  const dias360 = años * 360 + meses * 30 + Math.min(dias, 30);
  return { años, meses, dias, dias360 };
}

export default function CalculadoraIndemnizacion() {
  const [tipoDespidoId, setTipoDespidoId] = useState("arbitrario");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [comisiones, setComisiones] = useState(0);
  const [fechaInicio, setFechaInicio] = useState("2023-01-15");
  const [fechaCese, setFechaCese] = useState("2026-08-01");

  const tipoDespido = TIPOS_DESPIDO.find((t) => t.id === tipoDespidoId) ?? TIPOS_DESPIDO[0];

  const resultado = useMemo(() => {
    const sueldo = Number(sueldoBruto) || 0;
    const comisionPromedio = Number(comisiones) || 0;
    const sueldoMasComisiones = sueldo + comisionPromedio;
    const sextoGratificacion = sueldoMasComisiones / 6;
    const remuneracionComputable = sueldoMasComisiones + sextoGratificacion;

    const antiguedad = diferenciaFechas(fechaInicio, fechaCese);

    // Coeficiente de tiempo de servicio: solo meses completos (sin prorratear días sueltos)
    const mesesCompletos = antiguedad.años * 12 + antiguedad.meses;
    const coeficiente = mesesCompletos / 12;

    const baseSinTope = remuneracionComputable * 1.5 * coeficiente;
    const tope = remuneracionComputable * 12;
    const base = Math.min(baseSinTope, tope);
    const topeAlcanzado = baseSinTope > tope;

    const total = base * tipoDespido.factor;

    return {
      sueldoMasComisiones,
      sextoGratificacion,
      remuneracionComputable,
      antiguedad,
      mesesCompletos,
      coeficiente,
      base,
      topeAlcanzado,
      total,
    };
  }, [sueldoBruto, comisiones, fechaInicio, fechaCese, tipoDespido]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            ⚖️
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Indemnización
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Indemnización por despido (D.L. 728)</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Tipo de despido
              </label>
              <div className="space-y-2">
                {TIPOS_DESPIDO.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTipoDespidoId(t.id)}
                    className={`w-full text-left rounded-lg border px-3.5 py-2.5 transition-colors ${
                      tipoDespidoId === t.id
                        ? "border-red-700 bg-red-50"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <p
                      className={`text-sm font-medium ${
                        tipoDespidoId === t.id ? "text-red-800" : "text-slate-700"
                      }`}
                    >
                      {t.etiqueta}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{t.descripcion}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Sueldo bruto mensual
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={sueldoBruto}
                    onChange={(e) => setSueldoBruto(e.target.value)}
                    className={`${inputClase} pl-9 font-mono`}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Comisiones promedio
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={comisiones}
                    onChange={(e) => setComisiones(e.target.value)}
                    className={`${inputClase} pl-9 font-mono`}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-400">Promedio de los últimos 6 meses</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Inicio del periodo de trabajo
                </label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className={inputClase}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Fecha de cese
                </label>
                <input
                  type="date"
                  value={fechaCese}
                  onChange={(e) => setFechaCese(e.target.value)}
                  className={inputClase}
                />
              </div>
            </div>
            <p className="-mt-3 text-xs text-slate-400">
              Tiempo de servicio: {resultado.antiguedad.años} año(s), {resultado.antiguedad.meses}{" "}
              mes(es), {resultado.antiguedad.dias} día(s)
            </p>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Indemnización estimada
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.total))}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Sueldo básico + comisiones</span>
                <span className="font-mono">{formatearSoles(resultado.sueldoMasComisiones)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>+ 1/6 gratificación</span>
                <span className="font-mono">{formatearSoles(resultado.sextoGratificacion)}</span>
              </div>
              <div className="flex justify-between text-slate-300 border-t border-slate-700 pt-1.5 mt-1.5">
                <span>RC Total</span>
                <span className="font-mono">{formatearSoles(resultado.remuneracionComputable)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>
                  RC × 1.5 × {resultado.coeficiente.toFixed(2)} ({resultado.mesesCompletos} meses completos)
                </span>
                <span className="font-mono">{formatearSoles(resultado.base)}</span>
              </div>
              {tipoDespido.factor === 2 && (
                <div className="flex justify-between text-slate-300">
                  <span>× 2 (referencial, despido nulo)</span>
                  <span className="font-mono">{formatearSoles(resultado.total)}</span>
                </div>
              )}
              {resultado.topeAlcanzado && (
                <div className="flex justify-between text-amber-400 border-t border-slate-700 pt-1.5 mt-1.5">
                  <span>Tope de 12 remuneraciones aplicado</span>
                  <span className="font-mono">✓</span>
                </div>
              )}
              {tipoDespido.factor === 0 && (
                <p className="pt-1 text-slate-400">
                  El despido justificado por falta grave comprobada no genera derecho a
                  indemnización.
                </p>
              )}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. Fórmula general (D.L. 728): remuneración computable
          (sueldo + comisiones promedio + 1/6 de gratificación) × 1.5 × meses
          completos de servicio ÷ 12, con un tope de 12 remuneraciones. El coeficiente
          solo considera meses completos, sin prorratear días sueltos adicionales. En
          el despido nulo la ley prioriza la reposición en el puesto; el monto x2
          mostrado es solo referencial para el caso de que se opte por la
          indemnización. No incluye otros conceptos de la liquidación (CTS,
          gratificación, vacaciones). No constituye asesoría legal, contable ni
          laboral.
        </p>
      </div>
    </div>
  );
}